import React from 'react';
import { Monitor, Server, Globe } from 'lucide-react';
import type { LinkState } from './types';

const wsText = (s: LinkState): { label: string; ok: boolean } => {
  switch (s.ws) {
    case 'open': return { label: 'ws OPEN', ok: true };
    case 'connecting': return { label: '连接中', ok: false };
    case 'closed': return { label: `已断开${s.wsCloseCode ? `(code=${s.wsCloseCode})` : ''}`, ok: false };
  }
};

// 信令链路节点的三态：
//   ws=open                              → 绿"链路 OPEN"
//   ws=closed & serverReachable=true     → 黄"WS 已断·服务器在线"（多半是 host 离线，服务器进程正常）
//   ws=closed & serverReachable=false    → 红"信令服务器不可达"（服务器或本地网络真的挂了）
//   ws=closed & serverReachable=null     → 红"已断开"（未探测/探测中）
type LinkLevel = 'ok' | 'warn' | 'err';
const linkText = (s: LinkState): { label: string; level: LinkLevel } => {
  if (s.ws === 'open') return { label: '链路 OPEN', level: 'ok' };
  if (s.ws === 'connecting') return { label: '连接中', level: 'warn' };
  if (s.serverReachable === true) return { label: 'WS 已断·服务器在线', level: 'warn' };
  if (s.serverReachable === false) return { label: '信令服务器不可达', level: 'err' };
  return { label: '已断开', level: 'err' };
};

const hostText = (s: LinkState): { label: string; ok: boolean } => {
  switch (s.host) {
    case 'online': return { label: 'Host online', ok: true };
    case 'waiting': return { label: '等待主机上线', ok: false };
    case 'offline': return { label: 'Host offline', ok: false };
  }
};

// 三态颜色：ok=绿 / warn=黄 / err=红
const levelClass = (level: LinkLevel): string =>
  level === 'ok' ? 'text-emerald-400' : level === 'warn' ? 'text-amber-400' : 'text-red-400';
const levelSign = (level: LinkLevel): string =>
  level === 'ok' ? '══✓══' : level === 'warn' ? '══~══' : '══✗══';

// 连线段：三态
const Segment: React.FC<{ level: LinkLevel }> = ({ level }) => (
  <span className={`mx-1 font-mono text-xs ${levelClass(level)}`}>{levelSign(level)}</span>
);

const Node: React.FC<{ icon: React.ReactNode; title: string; sub: string; level: LinkLevel }> =
  ({ icon, title, sub, level }) => (
  <div className="flex flex-col items-center min-w-0">
    <div className={`flex items-center gap-1 text-[11px] font-medium ${level === 'ok' ? 'text-zinc-200' : 'text-zinc-400'}`}>
      {icon}{title}
    </div>
    <div className={`text-[9px] font-mono truncate ${levelClass(level)}`}>{sub}</div>
  </div>
);

// 二态→三态适配（host 节点继续用 ok/err 二态）
const boolLevel = (ok: boolean): LinkLevel => (ok ? 'ok' : 'err');

interface LinkStatusBarProps {
  link: LinkState;
  nowTick: number;
  clientTurn?: string;       // 浏览器实际 Allocate 的 TURN（stats.relayServer）
  hostTurn?: string;         // host 实际 Allocate 的 TURN（serverStats.host_relay_server）
  relayProtocol?: string;    // TCP/UDP
  transportType?: string;    // 'relay' 时才显示中转行
}

// 注：clientTurn/hostTurn/relayProtocol/transportType 字段保留在接口中（index/App 仍传入），
// 但 TURN 信息已下沉到每个 SessionRow，本组件不再渲染中转行，故不解构使用。
const LinkStatusBar: React.FC<LinkStatusBarProps> = ({ link, nowTick }) => {
  const ws = wsText(link);
  const linkInfo = linkText(link);
  const host = hostText(link);
  // 活跃灯：最近 3s 内有收发
  const active = link.lastMsgAt > 0 && (nowTick - link.lastMsgAt) < 3000;
  const agoSec = link.lastMsgAt > 0 ? ((nowTick - link.lastMsgAt) / 1000).toFixed(1) : '-';

  return (
    <div className="rounded-lg bg-white/3 border border-white/5 px-3 py-2 mb-3">
      <div className="flex items-center justify-center">
        <Node icon={<Monitor size={11} />} title="浏览器" sub={ws.label} level={boolLevel(ws.ok)} />
        <Segment level={boolLevel(ws.ok)} />
        <Node icon={<Server size={11} />} title="信令链路" sub={linkInfo.label} level={linkInfo.level} />
        <Segment level={ws.ok && host.ok ? 'ok' : 'err'} />
        <Node icon={<Globe size={11} />} title="主机" sub={host.label} level={boolLevel(host.ok)} />
      </div>
      <div className="flex items-center justify-center gap-3 mt-1.5 text-[9px] text-zinc-500 font-mono">
        <span>消息 ↑{link.msgSent} ↓{link.msgRecv}</span>
        <span>最近 {link.lastMsgType || '-'} · {agoSec}s前</span>
        <span className="flex items-center gap-1">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
          {active ? '活跃' : '静默'}
        </span>
      </div>
    </div>
  );
};

export default LinkStatusBar;
